/**
 * Belege-Tab der Buchhaltungsseite
 * Inkl. Drag & Drop, Foto-Upload, Auto-Match, Beleg-Vorschau, QR-Überweisung
 */

import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Upload, Trash2, X, Check,
  Receipt, Camera, Wand2, Loader2,
  Sparkles, Image, ZoomIn, AlertCircle, QrCode
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { useTabs } from '@/contexts/TabContext';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export function BelegeTab({ belege, selectedKlient, loadData, openChildTab }) {
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const belegeOhneZuordnung = belege.filter(b => !b.umsatz_id).length;

  // Foto-Upload State
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  // Drag & Drop State
  const [isDragging, setIsDragging] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const dropZoneRef = useRef(null);

  // Beleg-Vorschau State
  const [previewBeleg, setPreviewBeleg] = useState(null);

  // ==================== HANDLERS ====================

  const handleFotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (selectedKlient) formData.append('klient_id', selectedKlient);
      formData.append('auto_match', 'true');
      const response = await fetch(`${BACKEND_URL}/api/buchhaltung/belege/foto-upload`, {
        method: 'POST', body: formData
      });
      const result = await response.json();
      if (result.success) {
        setUploadResult(result);
        toast.success('Beleg erfolgreich erkannt!');
        loadData();
      } else {
        toast.error(result.error || 'Fehler bei der Erkennung');
      }
    } catch {
      toast.error('Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAutoMatch = async (belegId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/buchhaltung/belege/${belegId}/auto-match`, { method: 'POST' });
      const result = await response.json();
      if (result.success && result.vorschlaege?.length > 0) {
        const beste = result.vorschlaege[0];
        if (beste.konfidenz >= 70) {
          if (window.confirm(
            `Gefunden: ${beste.umsatz.empfaenger || beste.umsatz.verwendungszweck}\nBetrag: ${beste.umsatz.betrag?.toFixed(2)} €\nKonfidenz: ${beste.konfidenz}%\n\nZuordnen?`
          )) {
            await handleAutoZuordnen(belegId);
          }
        } else {
          toast.info(`${result.vorschlaege.length} mögliche Matches gefunden (beste: ${beste.konfidenz}%)`);
        }
      } else {
        toast.info('Kein passender Umsatz gefunden');
      }
    } catch {
      toast.error('Auto-Match fehlgeschlagen');
    }
  };

  const handleAutoZuordnen = async (belegId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/buchhaltung/belege/${belegId}/auto-zuordnen?min_konfidenz=70`, { method: 'POST' });
      const result = await response.json();
      if (result.success) {
        toast.success(`Automatisch zugeordnet (${result.konfidenz}% Konfidenz)`);
        loadData();
      } else {
        toast.warning(result.error || 'Zuordnung nicht möglich');
      }
    } catch {
      toast.error('Zuordnung fehlgeschlagen');
    }
  };

  const handleDeleteBeleg = async (id) => {
    if (!window.confirm('Beleg wirklich löschen?')) return;
    await fetch(`${BACKEND_URL}/api/buchhaltung/belege/${id}`, { method: 'DELETE' });
    toast.success('Beleg gelöscht');
    loadData();
  };

  const loadBelegPreview = async (belegId) => {
    try {
      const beleg = await fetch(`${BACKEND_URL}/api/buchhaltung/belege/${belegId}`).then(r => r.json());
      if (beleg.foto_base64) {
        setPreviewBeleg(beleg);
      } else {
        toast.info('Kein Foto für diesen Beleg verfügbar');
      }
    } catch {
      toast.error('Beleg konnte nicht geladen werden');
    }
  };

  // ==================== DRAG & DROP ====================

  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); if (e.currentTarget === e.target) setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };

  const handleDrop = async (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(f => f.type.startsWith('image/') || f.type === 'application/pdf');
    if (imageFiles.length === 0) { toast.error('Nur Bilder (JPEG, PNG, WebP) oder PDF erlaubt'); return; }
    const queueItems = imageFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9), file, status: 'pending', progress: 0, result: null
    }));
    setUploadQueue(prev => [...prev, ...queueItems]);
    for (const item of queueItems) { await processDroppedFile(item); }
  };

  const processDroppedFile = async (queueItem) => {
    setUploadQueue(prev => prev.map(q => q.id === queueItem.id ? { ...q, status: 'uploading', progress: 30 } : q));
    try {
      const formData = new FormData();
      formData.append('file', queueItem.file);
      if (selectedKlient) formData.append('klient_id', selectedKlient);
      formData.append('auto_match', 'true');
      setUploadQueue(prev => prev.map(q => q.id === queueItem.id ? { ...q, progress: 60 } : q));
      const response = await fetch(`${BACKEND_URL}/api/buchhaltung/belege/foto-upload`, { method: 'POST', body: formData });
      const result = await response.json();
      setUploadQueue(prev => prev.map(q => q.id === queueItem.id ? { ...q, status: result.success ? 'success' : 'error', progress: 100, result } : q));
      if (result.success) { toast.success(`${queueItem.file.name}: Beleg erkannt!`); loadData(); }
      else { toast.error(`${queueItem.file.name}: ${result.error || 'Fehler'}`); }
    } catch {
      setUploadQueue(prev => prev.map(q => q.id === queueItem.id ? { ...q, status: 'error', progress: 100 } : q));
      toast.error(`${queueItem.file.name}: Upload fehlgeschlagen`);
    }
  };

  const clearUploadQueue = () => setUploadQueue([]);

  // ==================== RENDER ====================

  return (
    <div className="space-y-4" data-testid="belege-tab">
      {/* Drag & Drop Zone */}
      <div 
        ref={dropZoneRef}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`relative transition-all duration-200 ${isDragging ? 'ring-2 ring-violet-500 ring-offset-2 bg-violet-50 rounded-xl' : ''}`}
      >
        {/* Drag Overlay */}
        {isDragging && (
          <div className="absolute inset-0 bg-violet-100/80 backdrop-blur-sm rounded-xl z-10 flex flex-col items-center justify-center border-2 border-dashed border-violet-500">
            <Upload className="w-12 h-12 text-violet-600 mb-3 animate-bounce" />
            <p className="text-lg font-medium text-violet-700">Belege hier ablegen</p>
            <p className="text-sm text-violet-500 mt-1">JPEG, PNG, WebP oder PDF</p>
          </div>
        )}
        
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <p className="text-sm text-slate-500">{belege.length} Belege</p>
            {belegeOhneZuordnung > 0 && (
              <p className="text-xs text-amber-600">{belegeOhneZuordnung} Belege ohne Umsatz-Zuordnung</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={handleFotoUpload} data-testid="foto-input" />
            <Button 
              size="sm" variant="default"
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="btn-foto-upload"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
              {uploading ? 'Analysiere...' : 'Foto aufnehmen'}
            </Button>
            <Button size="sm" onClick={() => openChildTab('/schnellerfassung/beleg', { title: 'Neuer Beleg', forceNew: true, params: { klientId: selectedKlient } })} data-testid="btn-beleg-anlegen">
              <Plus className="w-4 h-4 mr-2" /> Beleg erfassen
            </Button>
          </div>
        </div>

        {/* Upload Queue */}
        {uploadQueue.length > 0 && (
          <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-slate-700">Upload-Warteschlange</h4>
              <Button variant="ghost" size="sm" onClick={clearUploadQueue}><X className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-2">
              {uploadQueue.map(item => (
                <div key={item.id} className="flex items-center gap-3 p-2 bg-white rounded-lg">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    item.status === 'success' ? 'bg-green-100' : item.status === 'error' ? 'bg-red-100' : item.status === 'uploading' ? 'bg-violet-100' : 'bg-slate-100'
                  }`}>
                    {item.status === 'success' ? <Check className="w-4 h-4 text-green-600" /> :
                     item.status === 'error' ? <AlertCircle className="w-4 h-4 text-red-600" /> :
                     item.status === 'uploading' ? <Loader2 className="w-4 h-4 text-violet-600 animate-spin" /> :
                     <Upload className="w-4 h-4 text-slate-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{item.file.name}</p>
                    {item.status === 'uploading' && (
                      <div className="mt-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full bg-violet-500 transition-all" style={{ width: `${item.progress}%` }} />
                      </div>
                    )}
                    {item.result?.beleg && (
                      <p className="text-xs text-green-600 mt-1">{item.result.beleg.betrag?.toFixed(2)} € - {item.result.beleg.kategorie || 'Erkannt'}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty Drop Hint */}
        {!isDragging && belege.length === 0 && (
          <div className="mt-4 p-6 border-2 border-dashed border-slate-200 rounded-xl text-center bg-slate-50/50">
            <Upload className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Belege per Drag & Drop hierher ziehen</p>
            <p className="text-xs text-slate-400 mt-1">oder "Foto aufnehmen" für die Smartphone-Kamera</p>
          </div>
        )}

        {/* Upload Result */}
        {uploadResult && (
          <Card className="mt-4 border-violet-200 bg-gradient-to-r from-violet-50 to-purple-50">
            <CardContent className="py-4">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-violet-100 rounded-lg"><Sparkles className="w-6 h-6 text-violet-600" /></div>
                <div className="flex-1">
                  <h4 className="font-medium text-violet-900">KI-Analyse erfolgreich</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-sm">
                    <div><span className="text-slate-500">Betrag:</span><span className="ml-1 font-medium">{uploadResult.beleg?.betrag?.toFixed(2) || '-'} €</span></div>
                    <div><span className="text-slate-500">Datum:</span><span className="ml-1 font-medium">{uploadResult.beleg?.datum || '-'}</span></div>
                    <div><span className="text-slate-500">Kategorie:</span><span className="ml-1 font-medium">{uploadResult.beleg?.kategorie || '-'}</span></div>
                    <div><span className="text-slate-500">Konfidenz:</span><Badge variant="outline" className="ml-1 text-xs">{uploadResult.analyse?.konfidenz || 'mittel'}</Badge></div>
                  </div>
                  {uploadResult.auto_zugeordnet && (
                    <Badge className="mt-2 bg-green-100 text-green-700"><Check className="w-3 h-3 mr-1" /> Automatisch zugeordnet</Badge>
                  )}
                  {uploadResult.vorgeschlagener_klient && !selectedKlient && (
                    <p className="text-xs text-violet-600 mt-2">Klient erkannt: {uploadResult.vorgeschlagener_klient.name}</p>
                  )}
                  {/* Zur Überweisung Button */}
                  {uploadResult.beleg?.betrag && uploadResult.beleg?.typ !== 'einnahme' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-3 border-violet-300 text-violet-700 hover:bg-violet-100"
                      onClick={() => {
                        // Beleg-Daten für QR-Generator vorbereiten
                        const params = new URLSearchParams();
                        if (uploadResult.beleg?.betrag) params.set('betrag', uploadResult.beleg.betrag);
                        if (uploadResult.beleg?.empfaenger) params.set('empfaenger', uploadResult.beleg.empfaenger);
                        if (uploadResult.beleg?.verwendungszweck) params.set('verwendungszweck', uploadResult.beleg.verwendungszweck);
                        if (uploadResult.beleg?.iban) params.set('iban', uploadResult.beleg.iban);
                        openTab('/qr-ueberweisung', { title: 'QR-Überweisung' });
                        navigate(`/qr-ueberweisung?${params.toString()}`);
                      }}
                      data-testid="btn-zur-ueberweisung"
                    >
                      <QrCode className="w-4 h-4 mr-2" />
                      Zur Überweisung
                    </Button>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setUploadResult(null)}><X className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Belege Table */}
        {belege.length > 0 ? (
          <div className="mt-4 bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="px-4 py-2 text-slate-500 font-medium w-12"></th>
                    <th className="px-4 py-2 text-slate-500 font-medium">Datum</th>
                    <th className="px-4 py-2 text-slate-500 font-medium">Kategorie</th>
                    <th className="px-4 py-2 text-slate-500 font-medium">Absender/Empfänger</th>
                    <th className="px-4 py-2 text-slate-500 font-medium text-right">Betrag</th>
                    <th className="px-4 py-2 text-slate-500 font-medium">Zuordnung</th>
                    <th className="px-4 py-2 text-slate-500 font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {belege.slice(0, 50).map(b => (
                    <tr key={b.id} className={`hover:bg-slate-50 ${b.foto_upload ? 'bg-violet-50/30' : ''}`}>
                      <td className="px-2 py-2">
                        {b.foto_upload ? (
                          <button onClick={() => loadBelegPreview(b.id)} className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 hover:ring-2 hover:ring-violet-400 transition-all cursor-pointer flex items-center justify-center" title="Vorschau öffnen">
                            <Image className="w-5 h-5 text-violet-500" />
                          </button>
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center"><Receipt className="w-4 h-4 text-slate-300" /></div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          {b.foto_upload && <Camera className="w-3 h-3 text-violet-500" />}
                          {b.datum ? new Date(b.datum).toLocaleDateString('de-DE') : '-'}
                        </div>
                      </td>
                      <td className="px-4 py-2"><Badge variant="outline" className="text-xs">{b.kategorie || 'Sonstig'}</Badge></td>
                      <td className="px-4 py-2 text-slate-700 max-w-xs truncate">{b.typ === 'einnahme' ? b.absender : b.empfaenger || '-'}</td>
                      <td className={`px-4 py-2 text-right font-medium whitespace-nowrap ${b.typ === 'einnahme' ? 'text-green-700' : 'text-red-700'}`}>
                        {b.typ === 'einnahme' ? '+' : '-'}{(b.betrag || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                      </td>
                      <td className="px-4 py-2">
                        {b.umsatz_id ? (
                          <Badge className="bg-green-100 text-green-700 text-xs"><Check className="w-3 h-3 mr-1" /> {b.auto_zugeordnet ? 'Auto' : 'Zugeordnet'}</Badge>
                        ) : (
                          <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => handleAutoMatch(b.id)}>
                            <Wand2 className="w-3 h-3 mr-1" /> Auto-Match
                          </Button>
                        )}
                      </td>
                      <td className="px-4 py-2 flex gap-1">
                        {/* QR-Überweisung Button für Ausgaben */}
                        {b.typ !== 'einnahme' && b.betrag > 0 && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => {
                              const params = new URLSearchParams();
                              if (b.betrag) params.set('betrag', b.betrag);
                              if (b.empfaenger) params.set('empfaenger', b.empfaenger);
                              if (b.verwendungszweck) params.set('verwendungszweck', b.verwendungszweck);
                              if (b.iban) params.set('iban', b.iban);
                              openTab('/qr-ueberweisung', { title: 'QR-Überweisung' });
                              navigate(`/qr-ueberweisung?${params.toString()}`);
                            }}
                            title="QR-Code für Überweisung"
                            data-testid={`btn-qr-${b.id}`}
                          >
                            <QrCode className="w-4 h-4 text-violet-500" />
                          </Button>
                        )}
                        {b.foto_upload && (
                          <Button variant="ghost" size="sm" onClick={() => loadBelegPreview(b.id)} title="Vorschau"><ZoomIn className="w-4 h-4 text-violet-500" /></Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteBeleg(b.id)}><Trash2 className="w-4 h-4 text-slate-400" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : !uploadResult && uploadQueue.length === 0 && (
          <Card className="mt-4">
            <CardContent className="py-8 text-center">
              <Receipt className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Keine Belege vorhanden</p>
              <p className="text-sm text-slate-400 mt-1">Erfassen Sie Belege manuell oder per KI-Analyse</p>
            </CardContent>
          </Card>
        )}
      </div> {/* End Drop Zone */}

      {/* Beleg-Vorschau Modal */}
      {previewBeleg && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setPreviewBeleg(null)}>
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
              <div>
                <h3 className="font-semibold text-slate-800">Beleg-Vorschau</h3>
                <p className="text-sm text-slate-500">
                  {previewBeleg.datum && new Date(previewBeleg.datum).toLocaleDateString('de-DE')} - {previewBeleg.kategorie || 'Sonstig'} - {previewBeleg.betrag?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setPreviewBeleg(null)}><X className="w-5 h-5" /></Button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-120px)] bg-slate-100 flex items-center justify-center">
              {previewBeleg.foto_base64 ? (
                <img src={`data:image/jpeg;base64,${previewBeleg.foto_base64}`} alt="Beleg-Foto" className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg" />
              ) : (
                <div className="text-center py-12"><Receipt className="w-16 h-16 mx-auto text-slate-300 mb-4" /><p className="text-slate-500">Kein Foto verfügbar</p></div>
              )}
            </div>
            <div className="p-4 border-t border-slate-200 bg-white">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div><p className="text-slate-500">Empfänger</p><p className="font-medium">{previewBeleg.empfaenger || previewBeleg.absender || '-'}</p></div>
                <div><p className="text-slate-500">Kategorie</p><p className="font-medium">{previewBeleg.kategorie || '-'}</p></div>
                <div><p className="text-slate-500">Zuordnung</p><p className="font-medium">{previewBeleg.umsatz_id ? <span className="text-green-600">Zugeordnet</span> : <span className="text-amber-600">Offen</span>}</p></div>
                <div><p className="text-slate-500">KI-Analyse</p><p className="font-medium">{previewBeleg.ki_analysiert ? <span className="text-violet-600">Analysiert ({previewBeleg.ki_konfidenz})</span> : <span className="text-slate-400">-</span>}</p></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
